/**
 * Link Sanitizer Utilities
 *
 * Security-critical utilities for sanitizing markdown links and creating
 * safe markdown component overrides for react-markdown.
 *
 * SECURITY: This is the single source of truth for link sanitization.
 * All resource viewers MUST use these utilities.
 *
 * Originally in features/content/utils/link-sanitizer.tsx, moved to shared lib
 * as these utilities are used across multiple features.
 *
 * @see RESOURCE_VIEWER_EXTRACTION_ANALYSIS.md Section 2.1
 */

import type { ComponentPropsWithoutRef } from 'react';
import type { Components } from 'react-markdown';

/**
 * Props type for anchor element in react-markdown.
 * Includes the node property that react-markdown passes.
 */
type AnchorProps = ComponentPropsWithoutRef<'a'> & {
  node?: unknown;
  href?: string;
};

/**
 * Validates a URL for safe rendering in markdown content.
 *
 * Allowed protocols:
 * - https:// (secure external links)
 * - http:// (external links - will open in new tab with security attrs)
 * - / (relative paths within the app)
 * - # (anchor links within the page)
 *
 * Blocked:
 * - javascript: (XSS vector)
 * - data: (potential XSS/data exfiltration)
 * - vbscript: (legacy XSS vector)
 * - file: (local file access)
 * - All other protocols
 *
 * @param href - URL to validate
 * @returns true if URL is safe to render
 *
 * @example
 * ```typescript
 * isValidUrl('https://example.com'); // true
 * isValidUrl('/resources/guide'); // true
 * isValidUrl('#section-1'); // true
 * isValidUrl('javascript:alert(1)'); // false
 * isValidUrl('data:text/html,...'); // false
 * ```
 */
export function isValidUrl(href: string | undefined): boolean {
  if (!href) return false;

  return (
    href.startsWith('https://') ||
    href.startsWith('http://') ||
    href.startsWith('/') ||
    href.startsWith('#')
  );
}

/**
 * Checks if a URL points to an external domain.
 *
 * @param href - URL to check
 * @returns true if URL is external (http:// or https://)
 *
 * @example
 * ```typescript
 * isExternalUrl('https://example.com'); // true
 * isExternalUrl('http://example.com'); // true
 * isExternalUrl('/internal/page'); // false
 * isExternalUrl('#anchor'); // false
 * ```
 */
export function isExternalUrl(href: string): boolean {
  return href.startsWith('http://') || href.startsWith('https://');
}

/**
 * Security attributes for external links.
 * Prevents tabnabbing attacks when opening links in new tabs.
 *
 * - target="_blank": Opens in new tab
 * - rel="noopener": Prevents window.opener access (security)
 * - rel="noreferrer": Prevents Referer header leak (privacy)
 */
export const EXTERNAL_LINK_ATTRS = {
  target: '_blank' as const,
  rel: 'noopener noreferrer' as const,
};

/**
 * Creates a secure markdown components object for react-markdown.
 *
 * Features:
 * - Link sanitization (strips malicious protocols)
 * - External links open in new tab with security attributes
 * - Script elements are stripped (XSS prevention)
 * - Iframe elements are stripped (clickjacking prevention)
 *
 * @returns Components object for react-markdown
 *
 * @example
 * ```typescript
 * import ReactMarkdown from 'react-markdown';
 * import remarkGfm from 'remark-gfm';
 * import { createSecureMarkdownComponents } from '@/lib/link-sanitizer';
 *
 * const components = createSecureMarkdownComponents();
 *
 * <ReactMarkdown
 *   remarkPlugins={[remarkGfm]}
 *   components={components}
 * >
 *   {markdownContent}
 * </ReactMarkdown>
 * ```
 */
export function createSecureMarkdownComponents(): Components {
  return {
    // Security: Validate and sanitize links
    a: ({ node: _node, href, ...props }: AnchorProps) => {
      if (!isValidUrl(href)) {
        // Strip potentially malicious links - render as plain text
        return <span {...props} />;
      }

      // Add security attributes for external links
      const isExternal = isExternalUrl(href!);
      return (
        <a
          href={href}
          {...props}
          {...(isExternal && EXTERNAL_LINK_ATTRS)}
        />
      );
    },
    // Security: Strip script elements (XSS prevention)
    script: () => null,
    // Security: Strip iframe elements (clickjacking prevention)
    iframe: () => null,
  };
}
